import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Custom hook for streaming live stock data via Server-Sent Events (SSE).
 * 
 * @param {string} symbol - Stock symbol (e.g., "RELIANCE", "INFY")
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether to enable the stream (default: false)
 * @param {string} options.exchange - Exchange (default: "NSE")
 * @param {string} options.instrumentType - Instrument type (default: "EQ")
 * @param {function} options.onData - Callback when new data arrives
 * @param {function} options.onError - Callback when an error occurs
 * @param {function} options.onConnect - Callback when connection is established
 * @param {function} options.onDisconnect - Callback when connection is closed
 * @returns {Object} { isConnected, lastTick, error, connect, disconnect }
 */
export const useStockStream = (symbol, options = {}) => {
  const {
    enabled = false,
    exchange = 'NSE',
    instrumentType = 'EQ',
    interval = '1m',
    emitMode = 'close',
    onData,
    onError,
    onConnect,
    onDisconnect,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastTick, setLastTick] = useState(null);
  const [error, setError] = useState(null);
  const [tickCount, setTickCount] = useState(0);

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const maxReconnectAttempts = 5;
  const reconnectDelay = 3000; // 3 seconds

  /**
   * Build the SSE stream URL
   */
  const getStreamUrl = useCallback(() => {
    // Use the new SAT/ALGO API path - the proxy server will forward to the backend
    const baseUrl = '/api/sat/algo/v1/stocks';
    const params = new URLSearchParams({
      exchange,
      instrumentType,
      interval,
      emitMode,
    });
    return `${baseUrl}/${encodeURIComponent(symbol)}/stream?${params.toString()}`;
  }, [symbol, exchange, instrumentType, interval, emitMode]);

  /**
   * Connect to the SSE stream
   */
  const connect = useCallback(() => {
    if (!symbol) {
      console.warn('[useStockStream] No symbol provided');
      return;
    }

    // Close existing connection if any
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = getStreamUrl();
    console.log('[useStockStream] Connecting to:', url);

    try {
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('[useStockStream] Connection opened for', symbol);
        setIsConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        onConnect?.();
      };

      eventSource.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[useStockStream] Received tick:', data);
          setLastTick(data);
          setTickCount(prev => prev + 1);
          onData?.(data);
        } catch (e) {
          console.error('[useStockStream] Failed to parse data:', e, event.data);
        }
      };

      eventSource.onerror = (err) => {
        console.error('[useStockStream] Connection error:', err);
        setIsConnected(false);

        // Check if the connection was closed
        if (eventSource.readyState === EventSource.CLOSED) {
          setError('Connection closed');
          onDisconnect?.();

          // Attempt to reconnect
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            console.log(`[useStockStream] Reconnecting in ${reconnectDelay}ms (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`);

            reconnectTimeoutRef.current = setTimeout(() => {
              if (enabled) {
                connect();
              }
            }, reconnectDelay);
          } else {
            const errorMsg = 'Max reconnection attempts reached';
            setError(errorMsg);
            onError?.(new Error(errorMsg));
          }
        } else {
          setError('Stream error');
          onError?.(err);
        }
      };

    } catch (e) {
      console.error('[useStockStream] Failed to create EventSource:', e);
      setError(e.message);
      onError?.(e);
    }
  }, [symbol, getStreamUrl, enabled, onData, onError, onConnect, onDisconnect]);

  /**
   * Disconnect from the SSE stream
   */
  const disconnect = useCallback(() => {
    console.log('[useStockStream] Disconnecting from', symbol);

    // Clear reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    // Close event source
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
    onDisconnect?.();
  }, [symbol, onDisconnect]);

  /**
   * Reset tick count
   */
  const resetTickCount = useCallback(() => {
    setTickCount(0);
  }, []);

  // Connect/disconnect based on enabled flag
  useEffect(() => {
    if (enabled && symbol) {
      connect();
    } else {
      disconnect();
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      disconnect();
    };
  }, [enabled, symbol, exchange, instrumentType, interval, emitMode]); // Don't include connect/disconnect to avoid loops

  return {
    isConnected,
    lastTick,
    error,
    tickCount,
    connect,
    disconnect,
    resetTickCount,
  };
};

export default useStockStream;
